import React, { useEffect, useState } from 'react'
import Header from '../Header/Header';
import Sidebar from '../../pages/Sidebar';
import { useTranslation } from 'react-i18next';
import Cookies from "js-cookie";
import './CreateRole.scss';
import CMSPermission from '../CMSPermission/CMSPermssion';
import { createRoleAction, getRoleDetails, updateRoleDetailAction, getStateAction } from '../../actions/permissionAction';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import { HTTP_STATUS } from '../../constants/httpstatus/HttpsStatus';
import {
    FormControl,
    InputLabel,
    Select,
    Checkbox,
    ListItemText,
    MenuItem,
    Button,
} from '@mui/material';
import { hasAnyPermission } from '../../utils/permissionUtils';
import { PERMISSIONS } from '../../constants/permissions/Permissions';

const CreateRole = () => {

    const { i18n, t } = useTranslation();
    const language = Cookies.get("language");
    const { roleId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { roles = [] } = location.state || {};
    const isEditMode = !!roleId;
    const { states, loading } = useSelector((state) => state.getStatesReducer);
    const [roleName, setRoleName] = useState('');
    const [reportsTo, setReportsTo] = useState(null);
    const [cmsPermissionIds, setCmsPermissionIds] = useState([]);
    const [selectedStates, setSelectedStates] = useState([]);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useDispatch();

    const handleSubmit = async () => {
        if (isLoading) return;
        setIsLoading(true);

        const combinedPermissions = [...cmsPermissionIds];
        const payload = {
            role: roleName,
            permission_ids: combinedPermissions,
            reports_to: reportsTo,
            state_ids: selectedStates,
        };

        let response;
        if (isEditMode) {
            response = await dispatch(updateRoleDetailAction(roleId, payload));
        } else {
            response = await dispatch(createRoleAction(payload));
        }

        const { status, message } = response || {};

        if (status === HTTP_STATUS.SUCCESS) {
            NotificationManager.success(message)
            navigate('/roles_and_permission');
        } else {
            return;
        }
        setIsLoading(false);
    };

    useEffect(() => {
        handleChangeLanguage(language);
        dispatch(getStateAction(reportsTo ?? ''));
        if (isEditMode) {
            fetchRoleDetails(roleId);
        }
    }, [isEditMode, roleId]);

    useEffect(() => {
        if (reportsTo) {
            dispatch(getStateAction(reportsTo));
        }
    }, [reportsTo]);

    useEffect(() => {
        setIsFormValid(
            roleName.trim() !== '' &&
            reportsTo !== null &&
            selectedStates?.length > 0 &&
            cmsPermissionIds?.length > 0
        );
    }, [roleName, reportsTo, selectedStates, cmsPermissionIds]);

    const fetchRoleDetails = async (id) => {
        const { status, data } = await dispatch(getRoleDetails(id));
        if (status === 200) {
            setRoleName(data?.role ?? '');
            setCmsPermissionIds(data?.permissions?.map(p => p.id) ?? []);
            setReportsTo(data?.reports_to_id ?? null);
            const selectedStateIds = data?.states?.map(state => state.id);
            setSelectedStates(selectedStateIds);
        }
    };

    const validateAlphabets = (value) => {
        const regex = /^[A-Za-z\s]*$/;
        return regex.test(value);
    };

    const handleChangeLanguage = (language) => {
        i18n.changeLanguage(language);
    };

    const handleReportsChange = (id) => {
        setReportsTo(id ? parseInt(id) : null);
        setSelectedStates([])
    };

    const filteredRoles = isEditMode
        ? roles?.filter(role => role?.id !== parseInt(roleId))
        : roles;

    return (
        <div className='create-role-containers'>
            <div className='create-role-main'>
                <div className='create-role'>
                    <div className="header-navigation">
                        <p className="create-role-text">TVS &gt; {t('RolesAndPermission.rolesAndPermission')}</p>
                    </div>
                    <h3 className='create-role-heading'>
                        {isEditMode ? t('RolesAndPermission.editRole') : t('RolesAndPermission.createRole')}
                    </h3>
                    <div className='create-role-section'>
                        <div className='role-name-state'>
                            <div className='role-name'>
                                <label className='role-label required'>{t('RolesAndPermission.roleName')}</label>
                                <input
                                    type='text'
                                    placeholder={t('RolesAndPermission.roleNamePlaceholder')}
                                    value={roleName}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        if (validateAlphabets(value)) {
                                            setRoleName(value);
                                        }
                                    }}
                                />
                            </div>
                            <div className='reports-to'>
                                <label className='role-label required'>{t('RolesAndPermission.reportsTo')}</label>
                                <select
                                    value={reportsTo || ''}
                                    onChange={(e) => handleReportsChange(e.target.value)}
                                    className="report-dropdown-select"
                                >
                                    <option value="" disabled>{t('userManagement.selectRole')}</option>
                                    {filteredRoles && filteredRoles?.map((item) => (
                                        <option key={item?.id} value={item?.id}>
                                            {item?.role}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className='role-name-state'>
                            <div className='role-state'>
                                <label className='role-label required'>{t('RolesAndPermission.selectState')}</label>
                                <FormControl className="multiselect_part">
                                    <Select
                                        className="multipleSelect_part"
                                        labelId="state-label"
                                        id="state-select"
                                        name="state"
                                        multiple
                                        displayEmpty
                                        inputProps={{ className: 'multiselect_input' }}
                                        value={selectedStates || []}
                                        onChange={(event) => {
                                            const selectedOptions = event.target.value;

                                            if (selectedOptions.includes("all")) {
                                                setSelectedStates(selectedStates?.length === states?.length ? [] : states?.map((s) => s?.id));
                                            } else {
                                                setSelectedStates(selectedOptions);
                                            }
                                        }}
                                        renderValue={(selected) =>
                                            selected?.length === 0
                                                ? <span>{t('RolesAndPermission.selectState')}</span>
                                                : selected?.map((stateId) => states?.find((s) => s?.id === stateId)?.name)
                                                    .filter(Boolean)
                                                    .join(', ')
                                        }
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: '200px',
                                                },
                                            },
                                        }}
                                    >
                                        {(!states || states?.length === 0) ? (
                                            <MenuItem disabled>
                                                <ListItemText primary={t('common.noStatesAvailable')} />
                                            </MenuItem>
                                        ) : (
                                            <MenuItem className="partlist_menu" key="all" value="all">
                                                <Checkbox checked={selectedStates?.length === states?.length} />
                                                <ListItemText primary={t('common.selectAll')} />
                                            </MenuItem>
                                        )}
                                        {states?.map((state) => (
                                            <MenuItem className="partlist_menu" key={state?.id} value={state?.id}>
                                                <Checkbox checked={selectedStates.includes(state?.id)} />
                                                <ListItemText primary={state?.name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        <div className='permissions'>
                            <div>
                                <CMSPermission onPermissionsChange={setCmsPermissionIds} selectedPermissionIds={cmsPermissionIds || []} />
                            </div>
                        </div>
                        <div className='cancel-submit-btn'>
                            <button className='cancel-btn' onClick={() => navigate('/roles_and_permission')}>{t('common.cancel')}</button>
                            {hasAnyPermission([PERMISSIONS.CAN_UPDATE_ROLE_PERMISSION, PERMISSIONS.CAN_CREATE_ROLE_AND_PERMISSION]) && (
                                <button
                                    className='submit-btn'
                                    onClick={handleSubmit}
                                    disabled={!isFormValid || isLoading}
                                >
                                    {isLoading ? <span className="loader-btn"></span> : (isEditMode ? t('common.update') : t('common.submit'))}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CreateRole